import { styled } from '@linaria/react';
import { MultiSelect } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import type { OrganizationStatusesByProjectsAndBoards } from '../../../../../api/organization-client/organization-client.type';
import type { Workflow } from '../../../../../api/workflows-client/workflows-client.type';
import { useTranslation } from 'react-i18next';
import {
  createSafeMultiSelectChangeHandler,
  validateMultiSelectData,
  validateMultiSelectValue,
} from '../../../../../helpers/mantine-select-helpers';
import { redBase, skyDark, skyLightest } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { Table, TableColumn } from '../../table';
import {
  getMappedAndUnmappedStatusesForSubproject,
  getStatusesForSelectedBoards,
  maybeReplaceMappedStatusesFromWorkflowSteps,
} from '../workflows.helpers';
import { WorkflowAction } from './reducer';

type TableRow = {
  id: string;
  name: string;
  projectName: string;
  statusMapping: string | null;
  isProject: boolean;
  parentId?: string;
};

function EditBoards({
  state,
  dispatch,
  statusesByProjectsAndBoards,
}: {
  state: Workflow;
  dispatch: (action: WorkflowAction) => void;
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined;
}) {
  const i18n = useTranslation();
  // Prepare data for MultiSelect with null checks
  const multiSelectData = statusesByProjectsAndBoards
    ? Object.keys(statusesByProjectsAndBoards)
        .filter((projectId) => {
          const project = statusesByProjectsAndBoards[projectId];
          return project && project.name && project.subprojects;
        })
        .map((projectId) => {
          const project = statusesByProjectsAndBoards[projectId];
          return {
            key: projectId,
            group: project.name,
            items: Object.entries(project.subprojects)
              .filter(([_, subproject]) => subproject && subproject.name)
              .map(([subprojectId, subproject]) => ({
                key: subprojectId,
                value: subprojectId,
                label: subproject.name,
              })),
          };
        })
    : [];

  // Handler that includes the business logic
  const handleBoardsChange = (values: string[]) => {
    // Step 1: Get all statuses for the selected boards
    const allStatusesForSelectedBoards = statusesByProjectsAndBoards
      ? getStatusesForSelectedBoards(statusesByProjectsAndBoards, values)
      : [];
    // Step 2: Remove any statuses from state that have been deselected
    const newSteps = maybeReplaceMappedStatusesFromWorkflowSteps(state, allStatusesForSelectedBoards);
    // Step 4: Update the selected boards
    dispatch({
      type: 'SET_WORKFLOW',
      payload: { ...state, steps: newSteps, subprojects: values },
    });
  };

  // Create a safe handler with error handling
  const safeChangeHandler = createSafeMultiSelectChangeHandler(handleBoardsChange);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Text weight="bold" textKey="common.boards">
        {i18n.t('common.boards')}
      </Text>
      {statusesByProjectsAndBoards && (
        <>
          <MultiSelect
            placeholder={
              (state.subprojects || []).length
                ? undefined
                : i18n.t('data_management.workflows.search_and_add_boards', {
                    search: i18n.t('common.actions.search'),
                    add: i18n.t('common.actions.add'),
                    boards: i18n.t('common.boards'),
                  })
            }
            data={validateMultiSelectData(multiSelectData)}
            value={validateMultiSelectValue(state.subprojects)}
            onChange={safeChangeHandler}
            leftSection={<Icon name="search" size={16} color={skyDark} />}
            style={{ width: '100%' }}
            radius="xl"
            size="xs"
            maxDropdownHeight={400}
            searchable
            clearable
          />
          {Object.entries(statusesByProjectsAndBoards).map(([projectId, projectData]) => {
            const selectedSubprojectsFromProjectData = Object.entries(projectData.subprojects).filter(
              ([subprojectId, _]) => (state.subprojects || []).includes(subprojectId),
            );
            if (!selectedSubprojectsFromProjectData.length) return null;

            return (
              <span key={projectId}>
                <Text weight="bold" size="small">
                  {projectData.name}
                </Text>
                {selectedSubprojectsFromProjectData
                  .filter(([subprojectId, _]) => state.subprojects.includes(subprojectId))
                  .map(([subprojectId, { name }]) => {
                    return (
                      <BoardItem
                        key={subprojectId}
                        name={name}
                        onRemove={() => {
                          // Step 1: Filter out the deleted board from the selected boards
                          const remainingBoards = state.subprojects.filter((board) => board !== subprojectId);
                          // Step 2: Get all statuses for the remaining boards, if any
                          const allStatusesForSelectedBoards = getStatusesForSelectedBoards(
                            statusesByProjectsAndBoards,
                            remainingBoards,
                          );
                          // Step 3: Remove extra statuses from the workflow steps
                          const newSteps = maybeReplaceMappedStatusesFromWorkflowSteps(
                            state,
                            allStatusesForSelectedBoards,
                          );
                          // Step 4: Update the selected boards
                          dispatch({
                            type: 'SET_WORKFLOW',
                            payload: { ...state, steps: newSteps, subprojects: remainingBoards },
                          });
                        }}
                      />
                    );
                  })}
              </span>
            );
          })}
        </>
      )}
    </div>
  );
}

function formatDataForTable(
  selectedBoards: string[],
  mappedStatusBuckets: { [key: string]: string[] },
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards,
): TableRow[] {
  let tableData: TableRow[] = [];

  const filteredStatusesByProjectsAndBoards = Object.entries(statusesByProjectsAndBoards).filter(([_, projectData]) =>
    selectedBoards.some((board) => projectData.subprojects[board]),
  );

  filteredStatusesByProjectsAndBoards.forEach(([projectId, projectData]) => {
    tableData.push({
      id: projectId,
      name: projectData.name,
      projectName: projectData.name,
      statusMapping: null,
      isProject: true,
      parentId: undefined,
    });
    Object.entries(projectData.subprojects)
      .filter(([subprojectId, _]) => selectedBoards.includes(subprojectId))
      .forEach(([subprojectId, subprojectData]) => {
        const { mappedStatuses, unmappedStatuses } = getMappedAndUnmappedStatusesForSubproject(
          subprojectData.statuses,
          mappedStatusBuckets,
        );

        tableData.push({
          id: subprojectId,
          name: subprojectData.name,
          projectName: projectData.name,
          statusMapping: `${mappedStatuses.length} / ${unmappedStatuses.length}`,
          isProject: false,
          parentId: projectId,
        });
      });
  });
  return tableData;
}

function Boards({
  state,
  statusesByProjectsAndBoards,
}: {
  state: Workflow;
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined;
}) {
  const i18n = useTranslation();
  const columns: TableColumn<TableRow>[] = [
    {
      key: 'name',
      label: i18n.t('data_management.workflows.project_board_name', {
        project: i18n.t('common.project'),
        board: i18n.t('common.board'),
      }),
      width: '40%',
      sortable: true,
      render: (_value: TableRow[keyof TableRow], row: TableRow) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: row.isProject ? 0 : 12 }}>
          <Text weight={row.isProject ? 'bold' : 'medium'}>{row.name}</Text>
        </div>
      ),
    },
    {
      key: 'statusMapping',
      label: i18n.t('data_management.workflows.active_inactive_statuses', {
        active: i18n.t('common.active'),
        inactive: i18n.t('common.inactive'),
        statuses: i18n.t('common.status.statuses'),
      }),
      width: '30%',
      sortable: true,
      render: (_value: TableRow[keyof TableRow], row: TableRow) => row.statusMapping,
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Text size="large" weight="bold" style={{ padding: '4px 0' }} textKey="common.assigned_boards">
        {i18n.t('data_management.workflows.assigned_boards')}
      </Text>
      {statusesByProjectsAndBoards && (state.subprojects || []).length ? (
        <TableContainer>
          <Table
            data={formatDataForTable(
              state.subprojects,
              state.steps.reduce(
                (acc, step) => {
                  acc[step.name] = step.mappings.map((mapping) => mapping.external_name);
                  return acc;
                },
                {} as { [stepName: string]: string[] },
              ),
              statusesByProjectsAndBoards,
            )}
            columns={columns}
            selectedRowId={undefined}
            onRowClick={() => {}}
            getRowId={(row) => row.id}
            stickyHeader
            isChildRow={(row) => !row.isProject}
            getParentId={(row) => row.parentId || ''}
            highlightParentRows
          />
        </TableContainer>
      ) : (
        <EmptyStateContainer>
          <Icon name="check_circle_outlined" size={40} color={skyDark} />
          <Text size="large" color={skyDark} textKey="common.no_boards_selected">
            {i18n.t('data_management.workflows.no_boards_selected')}
          </Text>
        </EmptyStateContainer>
      )}
    </div>
  );
}

function BoardItem({ name, onRemove }: { name: string; onRemove: () => void }) {
  const { hovered, ref } = useHover();
  return (
    <ListItem ref={ref}>
      <Text size="small">{name}</Text>
      <Icon
        name="do_not_disturb_on"
        size={16}
        color={hovered ? redBase : skyDark}
        style={{ cursor: 'pointer' }}
        onClick={onRemove}
      />
    </ListItem>
  );
}

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 4px;
  border-radius: 4px;
  &:hover {
    background: ${skyLightest};
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

export { Boards, EditBoards };
